import { formatDueDate } from '@/lib/formatDateTime';

export type CetItemStatus = 'not_started' | 'in_progress' | 'completed';

export type StreamType = 'mock_exam' | 'practice_drill' | 'review_material';

export type MockExamItem = {
  id: string;
  title: string;
  weight: number;
  totalItems: number;
  status: CetItemStatus;
  score?: number;
};

export type SubjectItem = {
  id: string;
  subject: string;
  status: CetItemStatus;
  progress?: number;
};

export type CetBadge = {
  id: string;
  label: string;
  earned: boolean;
  imageSrc: string;
  requirementText?: string;
  awardedAt?: string;
};

export type WhatsNextItem = {
  id: string;
  title: string;
  streamType: StreamType;
  status: CetItemStatus;
  priority: number;
  progressPercent: number;
  imageSrc: string;
  detail?: string;
  href?: string;
};

export type CetStudentProfile = {
  firstName: string;
  fullName: string;
  grade: string;
  school: string;
};

export type ScoreContributions = {
  mockExams: number;
  practiceDrills: number;
  reviewMaterials: number;
};

export type ReadinessStatusStyle = {
  label: string;
  textClass: string;
  bgClass: string;
  ringClass: string;
};

export type CetReadinessSnapshot = {
  student: CetStudentProfile;
  mockExams: MockExamItem[];
  practiceDrills: SubjectItem[];
  reviewMaterials: SubjectItem[];
  badges: CetBadge[];
  whatsNext: WhatsNextItem[];
  heroNudge: string;
  nudges: {
    nextDrill: string;
    reviewResume: string;
  };
  scores: {
    mockExamScore: number;
    practiceDrillCompletion: number;
    reviewMaterialCompletion: number;
    cetReadinessScore: number;
    readinessLabel: string;
    contributions: ScoreContributions;
  };
  streamSummary: {
    mockExams: { completed: number; total: number; score: number; badgeEarned?: boolean };
    practiceDrills: { completed: number; total: number; completion: number; badgeEarned?: boolean };
    reviewMaterials: { completed: number; total: number; completion: number; badgeEarned?: boolean };
  };
  badgesEarnedCount?: number;
  badgesTotal?: number;
};

/**
 * API-facing DTOs (snake_case-friendly) so we can map backend payloads
 * into the existing UI snapshot shape without touching view components.
 */
export type ApiCetReadinessResponse = {
  student?: {
    first_name?: string;
    full_name?: string;
    grade?: string;
    school?: string;
  };
  mock_exams?: Array<{
    id: string;
    title: string;
    weight?: number;
    total_items: number;
    status: string;
    score?: number | null;
  }>;
  practice_drills?: Array<{
    id: string;
    subject: string;
    status: string;
    progress?: number | null;
  }>;
  review_materials?: Array<{
    id: string;
    subject: string;
    status: string;
    progress?: number | null;
  }>;
  badges?: Array<{
    id: string;
    label: string;
    earned: boolean;
    image_src: string;
    requirement_text?: string;
  }>;
  whats_next?: Array<{
    id: string;
    title: string;
    stream_type: StreamType;
    status: string;
    priority: number;
    progress_percent: number;
    image_src: string;
    detail?: string;
  }>;
  totals?: {
    practice_drills?: number;
    review_materials?: number;
  };
  hero_nudge?: string;
  nudges?: {
    next_drill?: string;
    review_resume?: string;
  };
};

export type ApiCetBadgeAward = {
  badge_type: string;
  datetime_created: string;
};

export type ApiCetStreamReadiness = {
  completed_count: number;
  total_count: number;
  badge_earned: boolean;
  completion_percent?: number | null;
  stream_contribution?: number | null;
  score?: number | null;
  completion?: number | null;
  weighted_score?: number | null;
};

export type ApiCetMyReadinessMockExamItem = {
  exam_type: string;
  exam_type_display: string;
  exam_id: number | null;
  exam_name: string;
  status: string;
  score_percent: number | null;
  sub_weight: number;
  session_id?: number | null;
  attempt_number?: number | null;
};

export type ApiCetMockExamsReadiness = ApiCetStreamReadiness & {
  stream_score: number;
  exams: ApiCetMyReadinessMockExamItem[];
};

export type ApiCetMyReadinessResponse = {
  student?: {
    first_name?: string;
    full_name?: string;
    grade?: string;
    school?: string;
  };
  cet_readiness_score?: number | null;
  readiness_label?: string;
  badges_earned?: number | string[];
  badges_total?: number;
  badges?: {
    earned?: ApiCetBadgeAward[];
    newly_awarded?: string[];
  };
  mock_exams?: ApiCetMockExamsReadiness;
  practice_drills?: ApiCetStreamReadiness;
  review_materials?: ApiCetStreamReadiness;
};

const MOCK_EXAM_WEIGHTS: Record<string, number> = {
  'pre-test': 0.1,
  upcat: 0.2,
  acet: 0.2,
  dcat: 0.2,
  ustet: 0.2,
  'post-test': 0.1,
};

const STREAM_FORMULA_WEIGHTS = {
  mockExams: 0.6,
  practiceDrills: 0.2,
  reviewMaterials: 0.2,
} as const;

function normalizeStatus(status: string | undefined): CetItemStatus {
  if (!status) return 'not_started';
  const normalized = status.trim().toLowerCase();
  if (normalized === 'completed' || normalized === 'complete' || normalized === 'done') {
    return 'completed';
  }
  if (normalized === 'in_progress' || normalized === 'in-progress' || normalized === 'ongoing') {
    return 'in_progress';
  }
  return 'not_started';
}

export function getReadinessLabel(score: number): string {
  if (score >= 80) return 'CET Ready';
  if (score >= 60) return 'Nearly Ready';
  if (score >= 40) return 'Building Readiness';
  return 'Getting Started';
}

export function getReadinessStatusStyle(score: number): ReadinessStatusStyle {
  const label = getReadinessLabel(score);

  if (score >= 80) {
    return {
      label,
      textClass: 'text-aralin-green',
      bgClass: 'bg-aralin-green/10',
      ringClass: 'stroke-aralin-green',
    };
  }
  if (score >= 60) {
    return {
      label,
      textClass: 'text-sky-700',
      bgClass: 'bg-sky-100',
      ringClass: 'stroke-sky-500',
    };
  }
  if (score >= 40) {
    return {
      label,
      textClass: 'text-amber-700',
      bgClass: 'bg-amber-50',
      ringClass: 'stroke-amber-400',
    };
  }
  return {
    label,
    textClass: 'text-gray-600',
    bgClass: 'bg-gray-100',
    ringClass: 'stroke-gray-400',
  };
}

export function getScoreBarColor(score: number): string {
  if (score >= 80) return 'bg-aralin-green';
  if (score >= 60) return 'bg-sky-500';
  return 'bg-amber-400';
}

export function getScoreTextColor(score: number): string {
  if (score >= 80) return 'text-aralin-green';
  if (score >= 60) return 'text-sky-600';
  return 'text-amber-600';
}

export function roundScore(score: number): number {
  return Math.round(score);
}

/** Bar fill colors used in the admin dashboard Sections tab table. */
export function getSectionsTabScoreBarColor(score: number): string {
  const rounded = roundScore(score);
  if (rounded > 80) return 'var(--aralin-color)';
  if (rounded >= 75) return 'var(--aralin-yellow)';
  return 'var(--aralin-red)';
}

export function computeMockExamScore(exams: MockExamItem[]): number {
  const completed = exams.filter((e) => e.status === 'completed' && e.score !== undefined);
  if (completed.length === 0) return 0;

  const weightedSum = completed.reduce(
    (sum, exam) => sum + (exam.score ?? 0) * (MOCK_EXAM_WEIGHTS[exam.id] ?? 0),
    0
  );
  const totalWeight = completed.reduce(
    (sum, exam) => sum + (MOCK_EXAM_WEIGHTS[exam.id] ?? 0),
    0
  );

  return totalWeight > 0 ? weightedSum / totalWeight : 0;
}

export function computeCompletionRate(
  items: { status: CetItemStatus }[],
  total: number
): number {
  const completed = items.filter((item) => item.status === 'completed').length;
  return total > 0 ? (completed / total) * 100 : 0;
}

export function computeCetReadinessScore(
  mockExamScore: number,
  practiceDrillCompletion: number,
  reviewMaterialCompletion: number
): number {
  return (
    mockExamScore * STREAM_FORMULA_WEIGHTS.mockExams +
    practiceDrillCompletion * STREAM_FORMULA_WEIGHTS.practiceDrills +
    reviewMaterialCompletion * STREAM_FORMULA_WEIGHTS.reviewMaterials
  );
}

export function computeScoreContributions(
  mockExamScore: number,
  practiceDrillCompletion: number,
  reviewMaterialCompletion: number
): ScoreContributions {
  return {
    mockExams: mockExamScore * STREAM_FORMULA_WEIGHTS.mockExams,
    practiceDrills: practiceDrillCompletion * STREAM_FORMULA_WEIGHTS.practiceDrills,
    reviewMaterials: reviewMaterialCompletion * STREAM_FORMULA_WEIGHTS.reviewMaterials,
  };
}

const mockExams: MockExamItem[] = [
  { id: 'pre-test', title: 'Pre-Test Exam', weight: 0.1, totalItems: 50, status: 'completed', score: 78 },
  { id: 'upcat', title: 'UPCAT Mock Exam', weight: 0.2, totalItems: 100, status: 'completed', score: 82 },
  { id: 'acet', title: 'ACET Mock Exam', weight: 0.2, totalItems: 85, status: 'completed', score: 68 },
  { id: 'dcat', title: 'DCAT Mock Exam', weight: 0.2, totalItems: 95, status: 'in_progress' },
  { id: 'ustet', title: 'USTET Mock Exam', weight: 0.2, totalItems: 90, status: 'not_started' },
  { id: 'post-test', title: 'Post-Test Exam', weight: 0.1, totalItems: 50, status: 'not_started' },
];

const practiceDrills: SubjectItem[] = [
  { id: 'english', subject: 'English', status: 'completed' },
  { id: 'math', subject: 'Math', status: 'completed' },
  { id: 'science', subject: 'Science', status: 'completed' },
  { id: 'filipino', subject: 'Filipino', status: 'completed' },
  { id: 'history', subject: 'History', status: 'completed' },
  { id: 'social-studies', subject: 'Social Studies', status: 'not_started' },
  { id: 'reading-comprehension', subject: 'Reading Comprehension', status: 'not_started' },
  { id: 'abstract-reasoning', subject: 'Abstract Reasoning', status: 'not_started' },
  { id: 'logic', subject: 'Logic', status: 'not_started' },
  { id: 'verbal', subject: 'Verbal', status: 'not_started' },
  { id: 'quantitative', subject: 'Quantitative', status: 'not_started' },
];

const reviewMaterials: SubjectItem[] = [
  { id: 'english', subject: 'English', status: 'completed', progress: 100 },
  { id: 'math', subject: 'Math', status: 'completed', progress: 100 },
  { id: 'science', subject: 'Science', status: 'completed', progress: 100 },
  { id: 'filipino', subject: 'Filipino', status: 'completed', progress: 100 },
  { id: 'history', subject: 'History', status: 'completed', progress: 100 },
  { id: 'reading-comprehension', subject: 'Reading Comprehension', status: 'completed', progress: 85 },
  { id: 'abstract-reasoning', subject: 'Abstract Reasoning', status: 'completed', progress: 90 },
  { id: 'logic', subject: 'Logic', status: 'in_progress', progress: 70 },
  { id: 'verbal', subject: 'Verbal', status: 'not_started', progress: 0 },
  { id: 'quantitative', subject: 'Quantitative', status: 'not_started', progress: 0 },
];

const PRACTICE_DRILL_TOTAL = 11;
const REVIEW_MATERIAL_TOTAL = 10;

function buildBadges(
  exams: MockExamItem[],
  drills: SubjectItem[]
): CetBadge[] {
  const examsRemaining = exams.filter((e) => e.status !== 'completed').length;
  const drillsRemaining = drills.filter((d) => d.status !== 'completed').length;

  return sortBadgesForDisplay([
    { id: 'review-mastery', label: 'Review Mastery', earned: true, imageSrc: '/cet-badge.png' },
    {
      id: 'practice-mastery',
      label: 'Practice Mastery',
      earned: false,
      imageSrc: '/cet-badge.png',
      requirementText: `${drillsRemaining} more drills`,
    },
    {
      id: 'cet-simulation',
      label: 'CET Simulation',
      earned: false,
      imageSrc: '/cet-badge.png',
      requirementText: `${examsRemaining} more exams`,
    },
    {
      id: 'cet-readiness',
      label: 'CET Readiness',
      earned: false,
      imageSrc: '/cet-badge.png',
      requirementText: 'Complete all streams',
    },
  ]);
}

const whatsNext: WhatsNextItem[] = [
  {
    id: 'dcat',
    title: 'DCAT Mock Exam',
    streamType: 'mock_exam',
    status: 'in_progress',
    priority: 1,
    progressPercent: 45,
    imageSrc: '/mock_exams.png',
  },
  {
    id: 'ustet',
    title: 'USTET Mock Exam',
    streamType: 'mock_exam',
    status: 'not_started',
    priority: 2,
    progressPercent: 0,
    imageSrc: '/mock_exams.png',
  },
  {
    id: 'logic',
    title: 'Logic Reviewer',
    streamType: 'review_material',
    status: 'in_progress',
    priority: 3,
    progressPercent: 70,
    imageSrc: '/daily_practice.svg',
    detail: '70% read',
  },
];

type SnapshotBuildInput = {
  student: CetStudentProfile;
  mockExamItems: MockExamItem[];
  practiceDrillItems: SubjectItem[];
  reviewMaterialItems: SubjectItem[];
  badgeItems?: CetBadge[];
  whatsNextItems?: WhatsNextItem[];
  practiceDrillTotal: number;
  reviewMaterialTotal: number;
  heroNudge?: string;
  nudges?: {
    nextDrill?: string;
    reviewResume?: string;
  };
};

function buildCetReadinessSnapshot(input: SnapshotBuildInput): CetReadinessSnapshot {
  const {
    student,
    mockExamItems,
    practiceDrillItems,
    reviewMaterialItems,
    badgeItems,
    whatsNextItems,
    practiceDrillTotal,
    reviewMaterialTotal,
    heroNudge,
    nudges,
  } = input;

  const mockExamScore = computeMockExamScore(mockExamItems);
  const practiceDrillCompletion = computeCompletionRate(practiceDrillItems, practiceDrillTotal);
  const reviewMaterialCompletion = computeCompletionRate(reviewMaterialItems, reviewMaterialTotal);
  const cetReadinessScore = computeCetReadinessScore(
    mockExamScore,
    practiceDrillCompletion,
    reviewMaterialCompletion
  );
  const contributions = computeScoreContributions(
    mockExamScore,
    practiceDrillCompletion,
    reviewMaterialCompletion
  );

  const mockCompleted = mockExamItems.filter((e) => e.status === 'completed').length;
  const drillCompleted = practiceDrillItems.filter((d) => d.status === 'completed').length;
  const reviewCompleted = reviewMaterialItems.filter((r) => r.status === 'completed').length;
  const displayScore = Math.round(cetReadinessScore);

  const nextDrill =
    nudges?.nextDrill ??
    practiceDrillItems.find((d) => d.status === 'not_started')?.subject ??
    'your next drill';
  const inProgressReview = reviewMaterialItems.find((r) => r.status === 'in_progress');
  const reviewResumeNudge =
    nudges?.reviewResume ??
    (inProgressReview
      ? `Pick up where you left off: ${inProgressReview.subject} (${inProgressReview.progress ?? 0}% read)`
      : 'Continue your reviewers');

  return {
    student,
    mockExams: sortMockExamsAlphabetically(mockExamItems),
    practiceDrills: practiceDrillItems,
    reviewMaterials: reviewMaterialItems,
    badges: badgeItems ?? buildBadges(mockExamItems, practiceDrillItems),
    whatsNext: whatsNextItems ?? whatsNext,
    heroNudge:
      heroNudge ?? 'Your DCAT Mock Exam is in progress — finishing it will boost your score.',
    nudges: {
      nextDrill,
      reviewResume: reviewResumeNudge,
    },
    scores: {
      mockExamScore,
      practiceDrillCompletion,
      reviewMaterialCompletion,
      cetReadinessScore: displayScore,
      readinessLabel: getReadinessLabel(displayScore),
      contributions,
    },
    streamSummary: {
      mockExams: {
        completed: mockCompleted,
        total: mockExamItems.length,
        score: mockExamScore,
      },
      practiceDrills: {
        completed: drillCompleted,
        total: practiceDrillTotal,
        completion: practiceDrillCompletion,
      },
      reviewMaterials: {
        completed: reviewCompleted,
        total: reviewMaterialTotal,
        completion: reviewMaterialCompletion,
      },
    },
  };
}

export function getCetReadinessSnapshot(): CetReadinessSnapshot {
  return buildCetReadinessSnapshot({
    student: {
      firstName: 'Maria',
      fullName: 'Maria Santos',
      grade: 'Grade 11',
      school: 'LSGH',
    },
    mockExamItems: mockExams,
    practiceDrillItems: practiceDrills,
    reviewMaterialItems: reviewMaterials,
    badgeItems: buildBadges(mockExams, practiceDrills),
    whatsNextItems: whatsNext,
    practiceDrillTotal: PRACTICE_DRILL_TOTAL,
    reviewMaterialTotal: REVIEW_MATERIAL_TOTAL,
    heroNudge: 'Your DCAT Mock Exam is in progress — finishing it will boost your score.',
  });
}

export function getCetReadinessSnapshotFromApi(payload: ApiCetReadinessResponse): CetReadinessSnapshot {
  const mappedMockExams: MockExamItem[] = (payload.mock_exams ?? []).map((exam) => ({
    id: exam.id,
    title: exam.title,
    weight: exam.weight ?? MOCK_EXAM_WEIGHTS[exam.id] ?? 0,
    totalItems: exam.total_items,
    status: normalizeStatus(exam.status),
    score: exam.score ?? undefined,
  }));

  const mappedPracticeDrills: SubjectItem[] = (payload.practice_drills ?? []).map((item) => ({
    id: item.id,
    subject: item.subject,
    status: normalizeStatus(item.status),
    progress: item.progress ?? undefined,
  }));

  const mappedReviewMaterials: SubjectItem[] = (payload.review_materials ?? []).map((item) => ({
    id: item.id,
    subject: item.subject,
    status: normalizeStatus(item.status),
    progress: item.progress ?? undefined,
  }));

  const mappedBadges: CetBadge[] | undefined = payload.badges?.map((badge) => ({
    id: badge.id,
    label: badge.label,
    earned: badge.earned,
    imageSrc: badge.image_src,
    requirementText: badge.requirement_text,
  }));

  const mappedWhatsNext: WhatsNextItem[] | undefined = payload.whats_next?.map((item) => ({
    id: item.id,
    title: item.title,
    streamType: item.stream_type,
    status: normalizeStatus(item.status),
    priority: item.priority,
    progressPercent: item.progress_percent,
    imageSrc: item.image_src,
    detail: item.detail,
  }));

  return buildCetReadinessSnapshot({
    student: {
      firstName: payload.student?.first_name ?? 'Student',
      fullName: payload.student?.full_name ?? 'Student',
      grade: payload.student?.grade ?? 'Grade 11',
      school: payload.student?.school ?? 'School',
    },
    mockExamItems: mappedMockExams.length > 0 ? mappedMockExams : mockExams,
    practiceDrillItems: mappedPracticeDrills.length > 0 ? mappedPracticeDrills : practiceDrills,
    reviewMaterialItems: mappedReviewMaterials.length > 0 ? mappedReviewMaterials : reviewMaterials,
    badgeItems: mappedBadges,
    whatsNextItems: mappedWhatsNext,
    practiceDrillTotal:
      payload.totals?.practice_drills ??
      (mappedPracticeDrills.length > 0 ? mappedPracticeDrills.length : PRACTICE_DRILL_TOTAL),
    reviewMaterialTotal:
      payload.totals?.review_materials ??
      (mappedReviewMaterials.length > 0 ? mappedReviewMaterials.length : REVIEW_MATERIAL_TOTAL),
    heroNudge: payload.hero_nudge,
    nudges: {
      nextDrill: payload.nudges?.next_drill,
      reviewResume: payload.nudges?.review_resume,
    },
  });
}

export const STREAM_TYPE_LABELS: Record<StreamType, string> = {
  mock_exam: 'Mock Exam',
  practice_drill: 'Drill',
  review_material: 'Reviewer',
};

export function formatPercent(value: number, decimals = 1): string {
  return `${value.toFixed(decimals)}%`;
}

export function formatPoints(value: number): string {
  return value.toFixed(2);
}

export const CONTRIBUTION_SEGMENTS = {
  mockExams: { label: 'Mock Exams', weight: '60%', color: 'bg-sky-500' },
  practiceDrills: { label: 'Practice Drills', weight: '20%', color: 'bg-violet-400' },
  reviewMaterials: { label: 'Review Materials', weight: '20%', color: 'bg-emerald-500' },
} as const;

export function formatStatusLabel(status: CetItemStatus): string {
  switch (status) {
    case 'not_started':
      return 'Not Started';
    case 'in_progress':
      return 'In Progress';
    case 'completed':
      return 'Completed';
  }
}

export function getWhatsNextActionLabel(status: CetItemStatus): string {
  switch (status) {
    case 'completed':
      return 'View Results';
    case 'in_progress':
      return 'Continue';
    case 'not_started':
      return 'Start';
  }
}

export function getMockExamRowValue(exam: MockExamItem): string {
  if (exam.status === 'completed' && exam.score !== undefined) {
    return formatMockExamScoreItems(exam);
  }
  if (exam.status === 'in_progress') {
    return 'In Progress';
  }
  return '—';
}

export function getMockExamCorrectItems(exam: MockExamItem): number {
  if (exam.score === undefined) return 0;
  return Math.round((exam.score / 100) * exam.totalItems);
}

export function formatMockExamScoreItems(exam: MockExamItem): string {
  return `${getMockExamCorrectItems(exam)}/${exam.totalItems}`;
}

export type LearningStreamModule = {
  id: string;
  title: string;
  type: string;
  dueDate: string;
  status: CetItemStatus;
  examId?: number;
  reviewerId?: number;
  progressPercent?: number;
};

export type LearningStream = {
  id: string;
  streamNumber: number;
  title: string;
  modules: LearningStreamModule[];
  progressPercent: number;
};

export type CetLearningStreamsSnapshot = {
  streams: LearningStream[];
};

export type ApiCetLearningStreamsResponse = {
  mock_exams?: {
    modules: Array<{
      exam_type: string;
      exam_type_display: string;
      exam_id: number | null;
      name: string;
      status: string;
      progress: number;
      exam_end_date?: string | null;
    }>;
  };
  practice_drills?: {
    modules: Array<{
      exam_id: number | null;
      name: string;
      status: string;
      progress: number;
      exam_end_date?: string | null;
    }>;
  };
  review_materials?: {
    modules: Array<{
      reviewer_id: number | null;
      name: string;
      subject: string;
      status: string;
      progress: number;
    }>;
  };
};

const PRACTICE_DRILL_DUE_DATES: Record<string, string> = {
  english: 'Jun 5, 2026',
  math: 'Jun 12, 2026',
  science: 'Jun 19, 2026',
  filipino: 'Jun 26, 2026',
  history: 'Jul 3, 2026',
  'social-studies': 'Jul 10, 2026',
  'reading-comprehension': 'Jul 17, 2026',
  'abstract-reasoning': 'Jul 24, 2026',
  logic: 'Jul 31, 2026',
  verbal: 'Aug 7, 2026',
  quantitative: 'Aug 14, 2026',
};

const REVIEW_MATERIAL_DUE_DATES: Record<string, string> = {
  english: 'Jun 8, 2026',
  math: 'Jun 15, 2026',
  science: 'Jun 22, 2026',
  filipino: 'Jun 29, 2026',
  history: 'Jul 6, 2026',
  'reading-comprehension': 'Jul 13, 2026',
  'abstract-reasoning': 'Jul 20, 2026',
  logic: 'Jul 27, 2026',
  verbal: 'Aug 3, 2026',
  quantitative: 'Aug 10, 2026',
};

function computeStreamProgressPercent(items: { status: CetItemStatus }[]): number {
  if (items.length === 0) return 0;
  const completed = items.filter((item) => item.status === 'completed').length;
  return Math.round((completed / items.length) * 100);
}

function formatLearningStreamDueDate(examEndDate: string | null | undefined): string {
  if (!examEndDate) return '—';
  return formatDueDate(examEndDate);
}

export function getCetLearningStreamsSnapshot(): CetLearningStreamsSnapshot {
  const mockExamModules: LearningStreamModule[] = mockExams.map((exam) => ({
    id: exam.id,
    title: exam.title,
    type: 'Mock Exam',
    dueDate: '—',
    status: exam.status,
  }));

  const practiceDrillModules: LearningStreamModule[] = practiceDrills.map((drill) => ({
    id: drill.id,
    title: `${drill.subject} Drill`,
    type: 'Practice Drill',
    dueDate: PRACTICE_DRILL_DUE_DATES[drill.id] ?? 'Aug 1, 2026',
    status: drill.status,
  }));

  const reviewModules: LearningStreamModule[] = reviewMaterials.map((review) => ({
    id: review.id,
    title: `${review.subject} Reviewer`,
    type: 'Review Material',
    dueDate: REVIEW_MATERIAL_DUE_DATES[review.id] ?? 'Aug 1, 2026',
    status: review.status,
  }));

  return {
    streams: [
      {
        id: 'mock-exams',
        streamNumber: 1,
        title: 'Mock Exams',
        modules: mockExamModules,
        progressPercent: computeStreamProgressPercent(mockExamModules),
      },
      {
        id: 'practice-drills',
        streamNumber: 2,
        title: 'Practice Drills',
        modules: practiceDrillModules,
        progressPercent: computeStreamProgressPercent(practiceDrillModules),
      },
      {
        id: 'reviews',
        streamNumber: 3,
        title: 'Reviews',
        modules: reviewModules,
        progressPercent: computeStreamProgressPercent(reviewModules),
      },
    ],
  };
}

export function mapExamTypeToModuleId(examType: string): string {
  return examType.replace(/_/g, '-');
}

export function sortMockExamsAlphabetically(exams: MockExamItem[]): MockExamItem[] {
  return [...exams].sort((a, b) => a.title.localeCompare(b.title));
}

export function mapMockExamsFromMyReadiness(
  mockStream: ApiCetMockExamsReadiness | undefined,
  fallback: MockExamItem[]
): MockExamItem[] {
  const exams = mockStream?.exams;
  if (!exams?.length) return sortMockExamsAlphabetically(fallback);

  return sortMockExamsAlphabetically(
    exams.map((exam) => {
      const id = mapExamTypeToModuleId(exam.exam_type);

      return {
        id,
        title: exam.exam_name || exam.exam_type_display,
        weight: exam.sub_weight ?? MOCK_EXAM_WEIGHTS[id] ?? 0,
        totalItems: 100,
        status: normalizeStatus(exam.status),
        score: exam.score_percent ?? undefined,
      };
    })
  );
}

export const CET_SIMULATION_BADGE_ID = 'cet-simulation';

const ALL_BADGE_IDS = [
  'review-mastery',
  'practice-mastery',
  'cet-simulation',
  'cet-readiness',
] as const;

const CET_BADGE_LABELS: Record<string, string> = {
  'review-mastery': 'Review Mastery',
  'practice-mastery': 'Practice Mastery',
  'cet-simulation': 'CET Simulation',
  'cet-readiness': 'CET Readiness',
};

const CET_BADGE_IMAGE_SRC: Record<string, string> = {
  'review-mastery': '/review_mastery_badge.png',
  'practice-mastery': '/practice_mastery_badge.png',
  'cet-simulation': '/cet_simulation_badge.png',
  'cet-readiness': '/cet_readiness_badge.png',
};

export const STREAM_BADGE_IDS: Record<string, string> = {
  'mock-exams': CET_SIMULATION_BADGE_ID,
  'practice-drills': 'practice-mastery',
  reviews: 'review-mastery',
};

export const CET_LEARNING_STREAM_IDS = ['mock-exams', 'practice-drills', 'reviews'] as const;

export type CetLearningStreamId = (typeof CET_LEARNING_STREAM_IDS)[number];

export const CET_LEARNING_STREAM_ROUTE = '/student/cet-learning-stream';

export function getCetLearningStreamHref(streamId: CetLearningStreamId): string {
  return `${CET_LEARNING_STREAM_ROUTE}?stream=${streamId}`;
}

export function isCetLearningStreamId(value: string | null): value is CetLearningStreamId {
  return CET_LEARNING_STREAM_IDS.includes(value as CetLearningStreamId);
}

const WHATS_NEXT_STREAM_IMAGES: Record<StreamType, string> = {
  mock_exam: '/mock_exams.png',
  practice_drill: '/daily_practice.svg',
  review_material: '/review.svg',
};

const WHATS_NEXT_STREAM_ORDER: Record<CetLearningStreamId, number> = {
  'mock-exams': 0,
  'practice-drills': 1,
  reviews: 2,
};

function learningStreamIdToType(streamId: string): StreamType | null {
  switch (streamId) {
    case 'mock-exams':
      return 'mock_exam';
    case 'practice-drills':
      return 'practice_drill';
    case 'reviews':
      return 'review_material';
    default:
      return null;
  }
}

function resolveModuleProgressPercent(streamModule: LearningStreamModule): number {
  if (typeof streamModule.progressPercent === 'number' && !Number.isNaN(streamModule.progressPercent)) {
    return Math.max(0, Math.min(100, streamModule.progressPercent));
  }
  if (streamModule.status === 'completed') return 100;
  if (streamModule.status === 'in_progress') return 0;
  return 0;
}

function buildWhatsNextHref(streamType: StreamType, streamModule: LearningStreamModule): string | undefined {
  if (streamType === 'mock_exam' && streamModule.examId != null) {
    return `/student/mock-exams/${streamModule.examId}`;
  }
  if (streamType === 'practice_drill' && streamModule.examId != null) {
    return `/student/daily-practice/${streamModule.examId}`;
  }
  if (streamType === 'review_material' && streamModule.reviewerId != null) {
    return `/student/reviewers/${streamModule.reviewerId}`;
  }
  return undefined;
}

export function buildWhatsNextFromLearningStreams(
  snapshot: CetLearningStreamsSnapshot
): WhatsNextItem[] {
  const candidates: Array<{
    streamModule: LearningStreamModule;
    streamId: string;
    streamType: StreamType;
    streamOrder: number;
  }> = [];

  for (const stream of snapshot.streams) {
    const streamType = learningStreamIdToType(stream.id);
    if (!streamType) continue;

    const streamOrder = WHATS_NEXT_STREAM_ORDER[stream.id as CetLearningStreamId] ?? 99;

    for (const streamModule of stream.modules) {
      if (streamModule.status === 'completed') continue;
      candidates.push({ streamModule, streamId: stream.id, streamType, streamOrder });
    }
  }

  const inProgress = candidates
    .filter((candidate) => candidate.streamModule.status === 'in_progress')
    .sort((a, b) => {
      const progressDiff =
        resolveModuleProgressPercent(b.streamModule) - resolveModuleProgressPercent(a.streamModule);
      if (progressDiff !== 0) return progressDiff;
      return a.streamOrder - b.streamOrder;
    });

  const notStarted = candidates
    .filter((candidate) => candidate.streamModule.status === 'not_started')
    .sort((a, b) => a.streamOrder - b.streamOrder);

  return [...inProgress, ...notStarted].slice(0, 3).map((candidate, index) => {
    const progressPercent = Math.round(resolveModuleProgressPercent(candidate.streamModule));

    return {
      id: `${candidate.streamId}-${candidate.streamModule.id}`,
      title: candidate.streamModule.title,
      streamType: candidate.streamType,
      status: candidate.streamModule.status,
      priority: index + 1,
      progressPercent,
      imageSrc: WHATS_NEXT_STREAM_IMAGES[candidate.streamType],
      href: buildWhatsNextHref(candidate.streamType, candidate.streamModule),
      detail:
        candidate.streamType === 'review_material' && candidate.streamModule.status === 'in_progress'
          ? `${progressPercent}% read`
          : undefined,
    };
  });
}

export function normalizeBadgeTypeId(badgeType: string): string {
  return badgeType.trim().toLowerCase().replace(/_/g, '-');
}

export function getCetBadgeImageSrc(badgeId: string, fallback?: string): string {
  return CET_BADGE_IMAGE_SRC[badgeId] ?? fallback ?? '/cet_readiness_badge.png';
}

export function getBadgeLabel(badgeId: string): string {
  return CET_BADGE_LABELS[badgeId] ?? badgeId;
}

export function formatBadgeAwardDate(isoDate: string): string {
  return new Date(isoDate).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function sortBadgesForDisplay(badges: CetBadge[]): CetBadge[] {
  const earned = badges
    .filter((badge) => badge.earned)
    .sort((a, b) => {
      if (a.awardedAt && b.awardedAt) {
        return new Date(a.awardedAt).getTime() - new Date(b.awardedAt).getTime();
      }
      if (a.awardedAt) return -1;
      if (b.awardedAt) return 1;
      return 0;
    });

  const locked = badges.filter((badge) => !badge.earned);

  return [...earned, ...locked];
}

function isKnownBadgeId(badgeId: string): badgeId is (typeof ALL_BADGE_IDS)[number] {
  return (ALL_BADGE_IDS as readonly string[]).includes(badgeId);
}

function buildBadgeRequirementText(
  badgeId: string,
  payload: ApiCetMyReadinessResponse
): string | undefined {
  switch (badgeId) {
    case 'cet-simulation': {
      const stream = payload.mock_exams;
      if (!stream) return undefined;
      const remaining = stream.total_count - stream.completed_count;
      return remaining > 0 ? `${remaining} more exam${remaining === 1 ? '' : 's'}` : undefined;
    }
    case 'practice-mastery': {
      const stream = payload.practice_drills;
      if (!stream) return undefined;
      const remaining = stream.total_count - stream.completed_count;
      return remaining > 0 ? `${remaining} more drill${remaining === 1 ? '' : 's'}` : undefined;
    }
    case 'review-mastery': {
      const stream = payload.review_materials;
      if (!stream) return undefined;
      const remaining = stream.total_count - stream.completed_count;
      return remaining > 0
        ? `${remaining} more reviewer${remaining === 1 ? '' : 's'}`
        : undefined;
    }
    case 'cet-readiness':
      return 'Complete all streams';
    default:
      return undefined;
  }
}

export function mapBadgesFromMyReadiness(payload: ApiCetMyReadinessResponse): CetBadge[] {
  const earnedAwards = payload.badges?.earned ?? [];
  const earnedById = new Map<string, ApiCetBadgeAward>();

  for (const award of earnedAwards) {
    const badgeId = normalizeBadgeTypeId(award.badge_type);
    if (!isKnownBadgeId(badgeId)) continue;

    const existing = earnedById.get(badgeId);
    if (
      !existing ||
      new Date(award.datetime_created).getTime() < new Date(existing.datetime_created).getTime()
    ) {
      earnedById.set(badgeId, award);
    }
  }

  const earnedBadges: CetBadge[] = [...earnedById.entries()]
    .sort(
      ([, a], [, b]) =>
        new Date(a.datetime_created).getTime() - new Date(b.datetime_created).getTime()
    )
    .map(([badgeId, award]) => ({
      id: badgeId,
      label: getBadgeLabel(badgeId),
      earned: true,
      imageSrc: getCetBadgeImageSrc(badgeId),
      awardedAt: award.datetime_created,
    }));

  const lockedBadges: CetBadge[] = ALL_BADGE_IDS.filter((badgeId) => !earnedById.has(badgeId)).map(
    (badgeId) => ({
      id: badgeId,
      label: getBadgeLabel(badgeId),
      earned: false,
      imageSrc: getCetBadgeImageSrc(badgeId),
      requirementText: buildBadgeRequirementText(badgeId, payload),
    })
  );

  return [...earnedBadges, ...lockedBadges];
}

export function mapNewlyAwardedBadges(payload: ApiCetMyReadinessResponse): CetBadge[] {
  const newlyAwarded = payload.badges?.newly_awarded ?? [];
  const earnedAwards = payload.badges?.earned ?? [];

  return newlyAwarded.map((badgeType) => {
    const id = normalizeBadgeTypeId(badgeType);
    const award = earnedAwards.find(
      (item) => normalizeBadgeTypeId(item.badge_type) === id
    );

    return {
      id,
      label: getBadgeLabel(id),
      earned: true,
      imageSrc: getCetBadgeImageSrc(id),
      awardedAt: award?.datetime_created,
    };
  });
}

export function findNewlyAwardedBadge(
  payload: ApiCetMyReadinessResponse,
  badgeType: string
): CetBadge | null {
  const targetId = normalizeBadgeTypeId(badgeType);
  return mapNewlyAwardedBadges(payload).find((badge) => badge.id === targetId) ?? null;
}

export function getBadgeForStream(streamId: string, badges: CetBadge[]): CetBadge | undefined {
  const badgeId = STREAM_BADGE_IDS[streamId];
  if (!badgeId) return undefined;
  return badges.find((badge) => badge.id === badgeId);
}

export function resolveBadgeEarnedCount(
  payload: ApiCetMyReadinessResponse,
  badges: CetBadge[]
): number {
  if (typeof payload.badges_earned === 'number' && !Number.isNaN(payload.badges_earned)) {
    return payload.badges_earned;
  }

  if (Array.isArray(payload.badges?.earned)) {
    return payload.badges.earned.length;
  }

  return badges.filter((badge) => badge.earned).length;
}

export function resolveBadgesTotal(
  payload: ApiCetMyReadinessResponse,
  badges: CetBadge[]
): number {
  if (typeof payload.badges_total === 'number' && !Number.isNaN(payload.badges_total)) {
    return payload.badges_total;
  }

  return badges.length || ALL_BADGE_IDS.length;
}

export function getCetReadinessSnapshotFromMyReadiness(
  payload: ApiCetMyReadinessResponse
): CetReadinessSnapshot {
  const fallback = getCetReadinessSnapshot();
  const mockStream = payload.mock_exams;
  const drillStream = payload.practice_drills;
  const reviewStream = payload.review_materials;
  const badges = mapBadgesFromMyReadiness(payload);

  const mockExamScore =
    mockStream?.stream_score ??
    mockStream?.score ??
    mockStream?.weighted_score ??
    fallback.scores.mockExamScore;
  const practiceDrillCompletion =
    drillStream?.completion_percent ??
    drillStream?.completion ??
    fallback.scores.practiceDrillCompletion;
  const reviewMaterialCompletion =
    reviewStream?.completion_percent ??
    reviewStream?.completion ??
    fallback.scores.reviewMaterialCompletion;
  const cetReadinessScore = payload.cet_readiness_score ?? fallback.scores.cetReadinessScore;
  const displayScore = Math.round(cetReadinessScore);
  const mockExamItems = mapMockExamsFromMyReadiness(mockStream, fallback.mockExams);

  return {
    ...fallback,
    student: {
      firstName: payload.student?.first_name ?? fallback.student.firstName,
      fullName: payload.student?.full_name ?? fallback.student.fullName,
      grade: payload.student?.grade ?? fallback.student.grade,
      school: payload.student?.school ?? fallback.student.school,
    },
    mockExams: mockExamItems,
    whatsNext: [],
    badges,
    badgesEarnedCount: resolveBadgeEarnedCount(payload, badges),
    badgesTotal: resolveBadgesTotal(payload, badges),
    scores: {
      mockExamScore,
      practiceDrillCompletion,
      reviewMaterialCompletion,
      cetReadinessScore: displayScore,
      readinessLabel: payload.readiness_label ?? getReadinessLabel(displayScore),
      contributions: computeScoreContributions(
        mockExamScore,
        practiceDrillCompletion,
        reviewMaterialCompletion
      ),
    },
    streamSummary: {
      mockExams: {
        completed: mockStream?.completed_count ?? fallback.streamSummary.mockExams.completed,
        total: mockStream?.total_count ?? fallback.streamSummary.mockExams.total,
        score: mockExamScore,
        badgeEarned: mockStream?.badge_earned,
      },
      practiceDrills: {
        completed: drillStream?.completed_count ?? fallback.streamSummary.practiceDrills.completed,
        total: drillStream?.total_count ?? fallback.streamSummary.practiceDrills.total,
        completion: practiceDrillCompletion,
        badgeEarned: drillStream?.badge_earned,
      },
      reviewMaterials: {
        completed:
          reviewStream?.completed_count ?? fallback.streamSummary.reviewMaterials.completed,
        total: reviewStream?.total_count ?? fallback.streamSummary.reviewMaterials.total,
        completion: reviewMaterialCompletion,
        badgeEarned: reviewStream?.badge_earned,
      },
    },
  };
}

export function getCetLearningStreamsSnapshotFromApi(
  payload: ApiCetLearningStreamsResponse
): CetLearningStreamsSnapshot {
  const apiMockExamModules = (payload.mock_exams?.modules ?? []).filter(
    (module) => module.exam_id != null
  );
  const mockExamModules: LearningStreamModule[] = apiMockExamModules.map((module) => {
    const id = mapExamTypeToModuleId(module.exam_type);
    return {
      id,
      title: module.name,
      type: module.exam_type_display,
      dueDate: formatLearningStreamDueDate(module.exam_end_date),
      status: normalizeStatus(module.status),
      examId: module.exam_id ?? undefined,
      progressPercent: module.progress,
    };
  });

  const apiPracticeDrillModules = (payload.practice_drills?.modules ?? []).filter(
    (module) => module.exam_id != null
  );
  const practiceDrillModules: LearningStreamModule[] = apiPracticeDrillModules.map((module) => ({
    id: `drill-${module.exam_id}`,
    title: module.name,
    type: 'Practice Drill',
    dueDate: formatLearningStreamDueDate(module.exam_end_date),
    status: normalizeStatus(module.status),
    examId: module.exam_id ?? undefined,
    progressPercent: module.progress,
  }));

  const apiReviewModules = (payload.review_materials?.modules ?? []).filter(
    (module) => module.reviewer_id != null
  );
  const reviewModules: LearningStreamModule[] = apiReviewModules.map((module) => ({
    id: `review-${module.reviewer_id}`,
    title: module.name,
    type: module.subject,
    dueDate: '—',
    status: normalizeStatus(module.status),
    reviewerId: module.reviewer_id ?? undefined,
    progressPercent: module.progress,
  }));

  const mockExamStream: LearningStream = {
    id: 'mock-exams',
    streamNumber: 1,
    title: 'Mock Exams',
    modules: mockExamModules,
    progressPercent: computeStreamProgressPercent(mockExamModules),
  };

  const practiceDrillStream: LearningStream = {
    id: 'practice-drills',
    streamNumber: 2,
    title: 'Practice Drills',
    modules: practiceDrillModules,
    progressPercent: computeStreamProgressPercent(practiceDrillModules),
  };

  const reviewMaterialsStream: LearningStream = {
    id: 'reviews',
    streamNumber: 3,
    title: 'Reviewers',
    modules: reviewModules,
    progressPercent: computeStreamProgressPercent(reviewModules),
  };

  return {
    streams: [mockExamStream, practiceDrillStream, reviewMaterialsStream],
  };
}
